import React, { useMemo } from 'react';
import { useStore } from 'effector-react';
import { type SceneConfig, SceneType } from '@types';
import { sendSuccess } from '@components/Noti';
import { Collapse } from '@components/Collapse';
import Title from '@components/Title';
import { $curScene, $sceneMapApi } from '@stores/dataStores';
import { $loadedScene } from '@stores/loadedStore';
import { MainPlayer } from '@stores/krpPlayerStores';
import ViewSlider, { type ViewSliderKey } from '@components/ViewSlider';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Tip from '@components/Tip';
import SidebarHeader from '../components/SidebarHeader';
import SidebarApplyTo from '../components/SidebarApplyTo';
import { showSceneModal } from '../../category';
import { viewDefault } from '../../view';
import InitialView from './InitialView';
import { loadScene } from '../../player';

interface Props {
  sceneType: SceneType.pano | SceneType.panoVideo;
}

type ResType = Required<SceneConfig>['defaultResolution'];

function PanoView({ sceneType }: Props) {
  const data = useStore($loadedScene)!;
  const { view, id, originTrack, transcodeTrack, config } = data;
  const krpPlayer = MainPlayer.get();

  const subTitle = {
    [SceneType.pano]: '图片',
    [SceneType.panoVideo]: '视频',
  }[sceneType];

  const [resItems, resValue] = useMemo(() => {
    const arr: DropdownItem<ResType>[] = [];
    let val: ResType = config.defaultResolution ?? 0;
    if (sceneType !== SceneType.panoVideo) return [arr, val];
    if (originTrack) {
      arr.push({ id: 0, name: '原画' });
    }
    if (transcodeTrack.super?.Status === 'SUCCESS') {
      arr.push({ id: 2, name: '4K' });
    }
    if (transcodeTrack.high?.Status === 'SUCCESS') {
      arr.push({ id: 3, name: '2K' });
    }
    if (transcodeTrack.standard?.Status === 'SUCCESS') {
      arr.push({ id: 1, name: '高清' });
    }

    // 如果当前选项不存在, 则设置为最高清晰度
    if (!arr.find((x) => x.id === val)) {
      val = arr[0]?.id ?? 0;
    }
    return [arr, val];
  }, [data, sceneType]);

  const handleFovChange = (params: { key: ViewSliderKey; value: number }) => {
    const krpKey = {
      left: 'fovmin',
      center: 'fov',
      right: 'fovmax',
    }[params.key];
    const dataKey = (
      {
        left: 'fovMin',
        center: 'fov',
        right: 'fovMax',
      } as const
    )[params.key];
    krpPlayer?.updateView({
      [krpKey]: params.value,
      fov: params.value,
    });
    $sceneMapApi.update((draft) => {
      draft.get(id)!.view[dataKey] = params.value;
    });
  };

  const handleHChange = (params: { key: ViewSliderKey; value: number }) => {
    if (params.key === 'center') return;
    const krpKey = {
      left: 'hlookatmin',
      right: 'hlookatmax',
    }[params.key];
    const dataKey = (
      {
        left: 'hLookAtMin',
        right: 'hLookAtMax',
      } as const
    )[params.key];
    krpPlayer?.updateView({
      [krpKey]: params.value,
      hlookat: params.value,
    });
    $sceneMapApi.update((draft) => {
      draft.get(id)!.view[dataKey] = params.value;
    });
  };

  const handleVChange = (params: { key: ViewSliderKey; value: number }) => {
    if (params.key === 'center') return;
    const krpKey = {
      left: 'vlookatmax',
      right: 'vlookatmin',
    }[params.key];
    const dataKey = (
      {
        left: 'vLookAtMax',
        right: 'vLookAtMin',
      } as const
    )[params.key];
    krpPlayer?.updateView({
      [krpKey]: -params.value,
      vlookat: -params.value,
    });
    $sceneMapApi.update((draft) => {
      draft.get(id)!.view[dataKey] = -params.value;
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [sceneType],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.view = view;
      });
    });
    sendSuccess('视角设置已应用到所选场景');
  };

  const handleFovApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [sceneType],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.view.fov = view.fov;
        draft.get(_id)!.view.fovMax = view.fovMax;
        draft.get(_id)!.view.fovMin = view.fovMin;
      });
    });
    sendSuccess('视角设置已应用到所选场景');
  };

  const handleLimitApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [sceneType],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.view.hLookAtMin = view.hLookAtMin;
        draft.get(_id)!.view.hLookAtMax = view.hLookAtMax;
        draft.get(_id)!.view.vLookAtMin = view.vLookAtMin;
        draft.get(_id)!.view.vLookAtMax = view.vLookAtMax;
      });
    });
    sendSuccess('视角设置已应用到所选场景');
  };

  const handleResAppleTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [sceneType],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.config.defaultResolution = resValue;
      });
    });
    sendSuccess('默认画质已应用到所选场景');
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>视角</Title>
            <span className="ml-5 self-end">{`（${subTitle}）`}</span>
          </div>
        }
        onResetClick={() => {
          $sceneMapApi.update((draft) => {
            draft.get(id)!.view = viewDefault;
          });
          loadScene({
            player: krpPlayer,
            data: $curScene.getState(),
          });
        }}
      />
      {sceneType === SceneType.pano && (
        <InitialView
          data={data}
          onTurnViewClick={() => {
            krpPlayer?.lookTo({
              fov: view.fov,
              hlookat: view.hLookAt,
              vlookat: view.vLookAt,
            });
          }}
        />
      )}
      <Collapse title="视角（FOV）范围">
        <ViewSlider
          min={0.1}
          max={180}
          step={0.1}
          leftValue={view.fovMin}
          centerValue={view.fov}
          rightValue={view.fovMax}
          leftTitle="最近"
          rightTitle="最远"
          centerTitle="初始"
          onChange={handleFovChange}
        />
        <SidebarApplyTo
          title="把视角FOV应用到..."
          className="mt-20"
          onClick={handleFovApplyTo}
        />
      </Collapse>
      <Collapse title="视角限制">
        <div>水平视角限制</div>
        <ViewSlider
          min={-180}
          max={180}
          leftValue={view.hLookAtMin}
          rightValue={view.hLookAtMax}
          leftTitle="最左"
          rightTitle="最右"
          onChange={handleHChange}
        />
        <div className="mt-20">垂直视角限制</div>
        <ViewSlider
          min={-90}
          max={90}
          leftValue={-view.vLookAtMax}
          rightValue={-view.vLookAtMin}
          leftTitle="最低"
          rightTitle="最高"
          onChange={handleVChange}
        />
        <SidebarApplyTo
          title="把视角限制应用到..."
          className="mt-20"
          onClick={handleLimitApplyTo}
        />
      </Collapse>
      {sceneType === SceneType.panoVideo && (
        <Collapse title="默认画质">
          <Dropdown
            items={resItems}
            selected={resValue}
            onChange={(val) => {
              $sceneMapApi.update((draft) => {
                draft.get(id)!.config.defaultResolution = val;
              });
            }}
          />
          <Tip className="mt-10 text-orange">
            注：如果视频原画画质出现卡顿或无法播放的情况，请确保全景视频格式是MP4，编码方式为H.264，且码率建议不超过30Mbps。如不符合，请重新上传
          </Tip>
          <SidebarApplyTo
            title="把默认画质应用到..."
            className="mt-20"
            onClick={handleResAppleTo}
          />
        </Collapse>
      )}
      <SidebarApplyTo title="把全部视角设置应用到..." onClick={handleApplyTo} />
    </>
  );
}

export default PanoView;
